import type {FormInstance} from 'antd';
import {useCallback, useEffect, useRef, useState} from 'react';

import type {LciFormValues} from '../../../services/interface/pcfTypes';
import {getPCFRecord} from '../../../services/modules/PCF/pcfAPI';
import usePCFStore from '../../../store/pcfStore/pcfStore';
import showMessage from '../../../utils/showMessage';
import {buildDefaultRecordData} from '../constants/pcfDefaultValues';
import {normalizeLegacyLciValues} from '../utils/lciValidation';
import type {PCFSection} from '../utils/pcfSave';
import {normalizeReferenceYearValue} from '../utils/referenceYearUtils';

import {usePCFRecordRoute} from './usePCFRecordRoute';

const normalizeSectionValues = (
  section: PCFSection,
  values: object
): object => {
  if (section !== 'goalAndScope') {
    if (section === 'lci') {
      return normalizeLegacyLciValues(values as Partial<LciFormValues>);
    }

    return values;
  }

  const goalAndScope = values as {referenceYear?: unknown};

  return {
    ...values,
    referenceYear: normalizeReferenceYearValue(goalAndScope.referenceYear)
  };
};

const getSectionValues = (
  section: PCFSection,
  recordData: ReturnType<typeof usePCFStore.getState>['recordData']
): object | undefined => {
  switch (section) {
    case 'goalAndScope':
      return recordData.goalAndScope;
    case 'lci':
      return recordData.lci;
    case 'impactConfiguration':
      return recordData.impactConfiguration;
    case 'results':
      return recordData.results?.interpretation;
    case 'report':
      return recordData.report;
    default:
      return undefined;
  }
};

const isStoreWarmForRoute = (
  routeId: string | null,
  section: PCFSection
): boolean => {
  if (!routeId) {
    return true;
  }

  const store = usePCFStore.getState();
  if (store.recordId !== routeId) {
    return false;
  }

  const values = getSectionValues(section, store.recordData);

  return values != null && Object.keys(values).length > 0;
};

export const usePCFStepHydration = (
  form: FormInstance,
  section: PCFSection
): ReturnType<typeof usePCFRecordRoute> & {
  isStepReady: boolean;
  reloadFromServer: () => Promise<void>;
  hydrateForm: () => void;
} => {
  const {
    routeId,
    recordId,
    isLoadingRecord,
    navigateToStep,
    syncUrlWithRecordId
  } = usePCFRecordRoute();
  const recordData = usePCFStore((state) => state.recordData);
  const setRecordFromApi = usePCFStore((state) => state.setRecordFromApi);
  const resetPCFStore = usePCFStore((state) => state.resetPCFStore);
  const hydratedRef = useRef(false);
  const hydratedRecordKeyRef = useRef<string | null>(null);

  const recordKey = recordId ?? routeId ?? 'new';

  const [isHydrated, setIsHydrated] = useState(() =>
    isStoreWarmForRoute(routeId, section)
  );

  const isStepReady = !routeId || (!isLoadingRecord && isHydrated);

  const hydrateForm = useCallback(() => {
    const values = getSectionValues(section, recordData);
    if (values != null && Object.keys(values).length > 0) {
      form.setFieldsValue(
        normalizeSectionValues(
          section,
          values
        ) as Parameters<FormInstance['setFieldsValue']>[0]
      );
    }
  }, [form, recordData, section]);

  useEffect(() => {
    hydratedRef.current = false;
    hydratedRecordKeyRef.current = null;
    setIsHydrated(isStoreWarmForRoute(routeId, section));
  }, [recordKey, routeId, section]);

  useEffect(() => {
    if (isLoadingRecord && routeId) {
      setIsHydrated(false);
    }
  }, [isLoadingRecord, routeId]);

  useEffect(() => {
    if (isLoadingRecord) {
      return;
    }

    if (!recordData.goalAndScope && !recordId && !routeId) {
      resetPCFStore(buildDefaultRecordData());
    }
  }, [
    recordData.goalAndScope,
    recordId,
    routeId,
    resetPCFStore,
    isLoadingRecord
  ]);

  useEffect(() => {
    if (isLoadingRecord) {
      return;
    }

    if (hydratedRecordKeyRef.current === recordKey && hydratedRef.current) {
      setIsHydrated(true);

      return;
    }

    const values = getSectionValues(section, recordData);
    if (values != null && Object.keys(values).length > 0) {
      form.setFieldsValue(
        normalizeSectionValues(
          section,
          values
        ) as Parameters<FormInstance['setFieldsValue']>[0]
      );
      hydratedRef.current = true;
      hydratedRecordKeyRef.current = recordKey;
    }

    setIsHydrated(true);
  }, [form, recordData, section, isLoadingRecord, recordKey]);

  const reloadFromServer = useCallback(async () => {
    if (!recordId) {
      showMessage({status: 'info', message: 'No saved study to reload yet.'});

      return;
    }

    setIsHydrated(false);

    try {
      const record = await getPCFRecord(recordId);
      setRecordFromApi(record);
      hydratedRef.current = false;
      hydratedRecordKeyRef.current = null;
      const values = getSectionValues(section, record.data);
      if (values != null) {
        form.setFieldsValue(
          normalizeSectionValues(
            section,
            values
          ) as Parameters<FormInstance['setFieldsValue']>[0]
        );
        hydratedRef.current = true;
        hydratedRecordKeyRef.current = recordId;
      }
      setIsHydrated(true);
      showMessage({status: 'success', message: 'Study reloaded from server.'});
    } catch {
      setIsHydrated(true);
      showMessage({
        status: 'error',
        message: 'Failed to reload study. Please try again.'
      });
    }
  }, [form, recordId, section, setRecordFromApi]);

  return {
    routeId,
    recordId,
    isLoadingRecord,
    isStepReady,
    navigateToStep,
    syncUrlWithRecordId,
    reloadFromServer,
    hydrateForm
  };
};
